// Foundations
import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';

// Models
import { MedicationType } from '@app/models/pet/medication-type.model';
import { ParsedPet } from '@app/models/pet/pet.model';
import { PetStatusType } from '@app/models/pet/pet-status.model';

// Services
import {
  GetMedicationsGQL,
  GetPetsWithStatusGQL,
} from '@app/services/graphql.service';
import { AuthService } from '@app/auth/auth.service';

@Injectable({
  providedIn: 'root',
})
export class PetService {
  // #region -> Pet Info <-
  // ----------

  petList: ParsedPet[] = [];
  petList$: BehaviorSubject<ParsedPet[]> = new BehaviorSubject([]);

  // Save local mapping of "Pet Profile Icon" based on "Pet ID".
  petProfileIconMappings: { [key: string]: string } = {};
  DEFAULT_PET_PROFILE_ICON = 'assets/images/Web_dog image_1.png';

  // For new pet
  newPetName: string;

  // ----------
  // #endregion

  // ///////////////////////////

  medicationTypeList: MedicationType[];

  constructor(
    private authService: AuthService,
    private getPetsWithStatusQuery: GetPetsWithStatusGQL,
    private getMedicationsQuery: GetMedicationsGQL
  ) {}

  // #region -> Pet Info <-
  // ----------

  fetchPetList() {
    this.authService
      .getCurrentAccountId()
      .pipe(
        switchMap((accountId) => {
          return this.getPetsWithStatusQuery.fetch({ accountId }).pipe(
            take(1),
            map((res) => res.data.getPetsWithStatus)
          );
        })
      )
      .subscribe({
        next: (list: ParsedPet[]) => this._parsePetList(list),
        error: (error) => {},
      });
  }

  getLocalProfileIcon(petId: string): string {
    if (petId in this.petProfileIconMappings) {
      return this.petProfileIconMappings[petId];
    }

    // Get default
    return this.DEFAULT_PET_PROFILE_ICON;
  }

  // ----------
  // #endregion

  // ///////////////////////////

  // #region -> Medication <-
  // ----------

  /**
   * Get a list of "medication types" based on the "search term".
   *
   * NOTE:
   * - An "empty" search term will return "all" types.
   *
   * @param searchTerm The search term string.
   * @returns The list of Medication Type info.
   */
  getMedicationTypes(searchTerm: string = '') {
    // Make API call to get the data
    this.medicationTypeList = [];
    return this.getMedicationsQuery.fetch({ search: searchTerm }).pipe(
      map((res) => res.data.getMedications),
      map((medicationTypeRecords) => {
        //
        medicationTypeRecords.forEach((record) =>
          this._mapToMedicationType(record)
        );

        //
        return this.medicationTypeList;
      })
    );
  }

  // ----------
  // #endregion

  // ///////////////////////////

  private _parsePetList(list: ParsedPet[]) {
    this.petList = list.filter((pet) => this._isValidPet(pet));

    // Decide the local profile images
    this._decideLocalPetProfileIcons(this.petList);

    // Cast the change
    this.petList$.next(this.petList);
  }

  private _isValidPet(pet: ParsedPet): boolean {
    // Only need "Pet" that is active and `status` is pending or passed on "Interview".
    return (
      pet &&
      pet.active &&
      (pet.statusId === PetStatusType.PASSED_INTERVIEW ||
        pet.statusId === PetStatusType.PENDING_INTERVIEW)
    );
  }

  private _decideLocalPetProfileIcons(list: ParsedPet[]) {
    list.map((pet: ParsedPet, index: number) => {
      this.petProfileIconMappings[pet.id] = `assets/images/Web_dog image_${
        (index % 4) + 1
      }.png`;
    });
  }

  private _mapToMedicationType(record) {
    //
    this.medicationTypeList.push({
      id: +record.id, // Convert it to `int`
      typeId: record.typeId,
      subTypeId: record.subTypeId,
      name: record.name,
      description: record.description,
    });
  }
}
